import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useProductos } from '../hooks/useProductos'
import ListaInventario from '../components/inventario/ListaInventario'
import FormProducto from '../components/inventario/FormProducto'
import { obtenerCategorias, guardarCategoria, eliminarCategoria, obtenerOCrearCategoria } from '../db/categorias'
import { guardarProducto, obtenerProductos } from '../db/productos'
import { puedeEliminarProducto, puedeEliminarCategoria } from '../db/integridad'
import ModalCategoria from '../components/inventario/ModalCategoria'
import { obtenerMovimientosInventario } from '../db/movimientos'
import { formatearDia, formatearHora } from '../utils/formato'
import TourBanner from '../components/layout/TourBanner'

// ── Períodos para movimientos ────────────────────────────────
const PERIODOS = [
  { label: 'Hoy',    dias: 0 },
  { label: '7 días', dias: 7 },
  { label: '30 días',dias: 30 },
  { label: 'Todo',   dias: null },
]

export default function Inventario() {
  const { productos, cargando, guardar, eliminar } = useProductos()

  // modo: inicio | catalogo | lista | form | movimientos | importar
  const [modo, setModo] = useState('inicio')
  const [categoriaActual, setCategoriaActual] = useState(null)
  const [productoEditar, setProductoEditar] = useState(null)
  const [confirmEliminar, setConfirmEliminar] = useState(null)
  const [verificandoEliminar, setVerificandoEliminar] = useState(false)
  const [bloqueadoEliminar, setBloqueadoEliminar] = useState(null) // null | string motivo

  const [dbCategorias, setDbCategorias] = useState([])

  useEffect(() => {
    obtenerCategorias().then(setDbCategorias)
  }, [])

  const categoriasConProductos = useMemo(() => {
    const map = {}
    for (const p of productos) {
      const cat = p.categoria || 'Sin categoría'
      if (!map[cat]) map[cat] = []
      map[cat].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [productos])

  const stockBajoCount = productos.filter(
    p => typeof p.stock === 'number' && p.stock <= 5
  ).length

  const productosDeLaCategoria = useMemo(() => {
    if (!categoriaActual) return productos
    return productos.filter(p => (p.categoria || 'Sin categoría') === categoriaActual)
  }, [productos, categoriaActual])

  function abrirNuevo() { setProductoEditar(null); setModo('form') }
  function abrirEditar(producto) { setProductoEditar(producto); setModo('form') }

  async function handleGuardar(producto) {
    await guardar(producto)
    setTimeout(() => setModo(categoriaActual ? 'lista' : 'catalogo'), 700)
  }

  async function abrirConfirmEliminar(producto) {
    setConfirmEliminar(producto)
    setBloqueadoEliminar(null)
    setVerificandoEliminar(true)
    const resultado = await puedeEliminarProducto(producto._id)
    setVerificandoEliminar(false)
    if (!resultado.ok) setBloqueadoEliminar(resultado.motivo)
  }

  function cerrarConfirmEliminar() {
    setConfirmEliminar(null)
    setBloqueadoEliminar(null)
    setVerificandoEliminar(false)
  }

  async function confirmarEliminar() {
    if (!confirmEliminar || bloqueadoEliminar) return
    await eliminar(confirmEliminar)
    cerrarConfirmEliminar()
  }

  // ── FORMULARIO ──────────────────────────────────────────────
  if (modo === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <FormProducto
          productoEditar={productoEditar}
          categoriaInicial={productoEditar ? undefined : categoriaActual}
          onGuardar={handleGuardar}
          onCancelar={() => setModo(categoriaActual ? 'lista' : 'catalogo')}
        />
      </div>
    )
  }

  // ── LISTA DE PRODUCTOS DE UNA CATEGORÍA ─────────────────────
  if (modo === 'lista') {
    return (
      <div className="flex-1 flex flex-col">
        <Header
          titulo={categoriaActual}
          subtitulo={`${productosDeLaCategoria.length} productos`}
          onVolver={() => { setCategoriaActual(null); setModo('catalogo') }}
          onNuevo={abrirNuevo}
          stockBajoCount={stockBajoCount}
        />
        <div className="flex-1 overflow-y-auto">
          {cargando ? <Spinner /> : (
            <ListaInventario
              productos={productosDeLaCategoria}
              onEditar={abrirEditar}
              onEliminar={abrirConfirmEliminar}
            />
          )}
        </div>
        <ModalConfirmar
          producto={confirmEliminar}
          verificando={verificandoEliminar}
          bloqueado={bloqueadoEliminar}
          onConfirmar={confirmarEliminar}
          onCancelar={cerrarConfirmEliminar}
        />
      </div>
    )
  }

  // ── CATÁLOGO (categorías) ────────────────────────────────────
  if (modo === 'catalogo') {
    return (
      <div className="flex-1 flex flex-col">
        <Header
          titulo="Productos"
          subtitulo={`${productos.length} productos · ${categoriasConProductos.length} categorías`}
          onVolver={() => setModo('inicio')}
          onNuevo={abrirNuevo}
          stockBajoCount={stockBajoCount}
        />
        <div className="flex-1 overflow-y-auto">
          {cargando ? <Spinner /> : categoriasConProductos.length === 0 ? (
            <div className="text-center py-20 text-tienda-muted">
              <p className="text-5xl mb-4">📦</p>
              <p className="text-xl font-medium">Sin productos aún</p>
              <p className="text-sm mt-1">Toca "+ Nuevo" para empezar</p>
            </div>
          ) : (
            <div className="p-4 grid grid-cols-2 gap-3">
              {categoriasConProductos.map(([cat, prods]) => {
                const agotados = prods.filter(p => typeof p.stock === 'number' && p.stock <= 0).length
                const bajos = prods.filter(p => typeof p.stock === 'number' && p.stock > 0 && p.stock <= 5).length
                const dbCat = dbCategorias.find(c => c.nombre === cat)
                const emoji = dbCat?.emoji || prods[0]?.emoji || '📦'

                return (
                  <button
                    key={cat}
                    onClick={() => { setCategoriaActual(cat); setModo('lista') }}
                    className="card p-4 text-left active:scale-95 transition-transform duration-100 flex flex-col gap-2"
                  >
                    <span className="text-4xl">{emoji}</span>
                    <div>
                      <p className="font-bold text-tienda-text text-base leading-tight">{cat}</p>
                      <p className="text-tienda-muted text-sm mt-0.5">
                        {prods.length} {prods.length === 1 ? 'producto' : 'productos'}
                      </p>
                    </div>
                    {(agotados > 0 || bajos > 0) && (
                      <div className="flex gap-1 flex-wrap">
                        {agotados > 0 && (
                          <span className="badge-danger">{agotados} agotado{agotados > 1 ? 's' : ''}</span>
                        )}
                        {bajos > 0 && (
                          <span className="text-xs bg-tienda-accent/20 text-tienda-accent font-bold px-2 py-0.5 rounded-full">
                            {bajos} bajo
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        <ModalConfirmar
          producto={confirmEliminar}
          verificando={verificandoEliminar}
          bloqueado={bloqueadoEliminar}
          onConfirmar={confirmarEliminar}
          onCancelar={cerrarConfirmEliminar}
        />
      </div>
    )
  }

  // ── MOVIMIENTOS ──────────────────────────────────────────────
  if (modo === 'movimientos') {
    return (
      <div className="flex-1 flex flex-col">
        <Header
          titulo="Entradas / Salidas"
          subtitulo="Historial de movimientos"
          onVolver={() => setModo('inicio')}
        />
        <SeccionMovimientos />
      </div>
    )
  }

  // ── IMPORTAR / EXPORTAR ──────────────────────────────────────
  if (modo === 'importar') {
    return (
      <div className="flex-1 flex flex-col">
        <Header
          titulo="Importar"
          subtitulo="Plantilla CSV de productos"
          onVolver={() => setModo('inicio')}
        />
        <div className="flex-1 overflow-y-auto p-4">
          <SeccionImportar />
        </div>
      </div>
    )
  }

  // ── CATEGORÍAS ──────────────────────────────────────────────
  if (modo === 'categorias-config') {
    return (
      <div className="flex-1 flex flex-col">
        <Header
          titulo="Categorías"
          subtitulo="Organiza tus productos por tipo"
          onVolver={() => setModo('inicio')}
        />
        <div className="flex-1 overflow-y-auto p-4">
          <SeccionCategorias />
        </div>
      </div>
    )
  }

  // ── HOME — 4 tarjetas ────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col bg-tienda-bg">
      <div className="px-4 pt-5 pb-4 bg-tienda-surface border-b border-tienda-border flex-shrink-0">
        <h1 className="text-2xl font-bold text-tienda-text">Mis Productos</h1>
        <p className="text-tienda-muted text-sm mt-0.5">
          {productos.length} productos{stockBajoCount > 0 && ` · ${stockBajoCount} con stock bajo`}
        </p>
      </div>

      <TourBanner
        moduleKey="misProductos"
        titulo="Administra tu catálogo de productos"
        descripcion="Agrega y edita productos, registra entradas de stock y carga tu inventario desde una hoja de cálculo."
      />

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {/* Card 1 — Productos */}
        <button
          onClick={() => setModo('catalogo')}
          className="card p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform"
        >
          <div className="w-14 h-14 rounded-2xl bg-tienda-accent/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-tienda-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <path d="M9 12h6M9 16h4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-tienda-text font-bold text-lg">Productos</p>
            <p className="text-tienda-muted text-sm mt-0.5">
              Agrega, edita y organiza tus productos por categorías
            </p>
          </div>
          <svg className="w-5 h-5 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>

        {/* Card 2 — Categorías */}
        <button
          onClick={() => setModo('categorias-config')}
          className="card p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform"
        >
          <div className="w-14 h-14 rounded-2xl bg-tienda-accent/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-tienda-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-tienda-text font-bold text-lg">Categorías</p>
            <p className="text-tienda-muted text-sm mt-0.5">
              Crea y edita las categorías para organizar tus productos
            </p>
          </div>
          <svg className="w-5 h-5 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>

        {/* Card 3 — Entradas / Salidas */}
        <button
          onClick={() => setModo('movimientos')}
          className="card p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform"
        >
          <div className="w-14 h-14 rounded-2xl bg-tienda-success/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-tienda-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" />
              <path d="M19 5H5" strokeDasharray="3 3" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-tienda-text font-bold text-lg">Entradas / Salidas</p>
            <p className="text-tienda-muted text-sm mt-0.5">
              Historial de movimientos de inventario
            </p>
          </div>
          <svg className="w-5 h-5 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>

        {/* Card 4 — Importar / Exportar */}
        <button
          onClick={() => setModo('importar')}
          className="card p-5 flex items-center gap-4 text-left active:scale-[0.98] transition-transform"
        >
          <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-tienda-text font-bold text-lg">Importar</p>
            <p className="text-tienda-muted text-sm mt-0.5">
              Carga tu inventario en masa desde una plantilla CSV
            </p>
          </div>
          <svg className="w-5 h-5 text-tienda-muted flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// ── SECCIÓN MOVIMIENTOS ──────────────────────────────────────

function SeccionMovimientos() {
  const [movimientos, setMovimientos] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [periodo, setPeriodo] = useState(1)

  const cargar = useCallback(async () => {
    setCargando(true)
    setMovimientos(await obtenerMovimientosInventario(PERIODOS[periodo].dias))
    setCargando(false)
  }, [periodo])

  useEffect(() => { cargar() }, [cargar])

  if (cargando || !movimientos) {
    return <Spinner />
  }

  const entradas = movimientos.filter(m => m.tipo === 'entrada').length
  const salidas  = movimientos.filter(m => m.tipo === 'salida').length

  return (
    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      {/* Selector de período */}
      <div className="flex gap-2">
        {PERIODOS.map((p, i) => (
          <button
            key={i}
            onClick={() => setPeriodo(i)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors
              ${periodo === i
                ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Resumen */}
      {movimientos.length > 0 && (
        <div className="card p-4 flex gap-4">
          <div className="flex-1">
            <p className="text-tienda-muted text-xs mb-1">Entradas</p>
            <p className="text-tienda-success font-bold text-2xl">+{entradas}</p>
          </div>
          <div className="w-px bg-tienda-border" />
          <div className="flex-1">
            <p className="text-tienda-muted text-xs mb-1">Salidas</p>
            <p className="text-tienda-danger font-bold text-2xl">-{salidas}</p>
          </div>
          <div className="w-px bg-tienda-border" />
          <div className="flex-1">
            <p className="text-tienda-muted text-xs mb-1">Total</p>
            <p className="text-tienda-text font-bold text-2xl">{movimientos.length}</p>
          </div>
        </div>
      )}

      {movimientos.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-tienda-surface border border-tienda-border flex items-center justify-center text-3xl">
            📋
          </div>
          <p className="text-tienda-muted">Sin movimientos en este período</p>
        </div>
      ) : (
        <div className="space-y-2">
          {movimientos.map(m => (
            <MovimientoFila key={m._id} movimiento={m} />
          ))}
        </div>
      )}
    </div>
  )
}

function MovimientoFila({ movimiento: m }) {
  const esEntrada = m.tipo === 'entrada'
  return (
    <div className="card px-4 py-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
        ${esEntrada ? 'bg-tienda-success/15' : 'bg-tienda-danger/15'}`}>
        <svg className={`w-4 h-4 ${esEntrada ? 'text-tienda-success' : 'text-tienda-danger'}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          {esEntrada
            ? <path d="M12 5v14M5 12l7 7 7-7" />
            : <path d="M12 19V5M5 12l7-7 7 7" />}
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-tienda-text text-sm font-semibold truncate">{m.productoNombre}</p>
        <p className="text-tienda-muted text-xs">
          {esEntrada ? 'Entrada manual' : `Venta · ${m.ventaFolio}`}
          {' · '}{formatearHora(m.fecha)}
        </p>
        <p className="text-tienda-muted text-xs">Stock: {m.stockAnterior} → {m.stockNuevo}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`font-bold text-base ${esEntrada ? 'text-tienda-success' : 'text-tienda-danger'}`}>
          {esEntrada ? '+' : '-'}{m.cantidad}
        </p>
        <p className="text-tienda-muted text-xs">{formatearDia(m.fecha)}</p>
      </div>
    </div>
  )
}

// ── SECCIÓN IMPORTAR / EXPORTAR ──────────────────────────────

function parsearCSV(texto) {
  const lineas = texto.split(/\r?\n/).filter(l => l.trim())
  return lineas.map(linea => {
    const campos = []
    let actual = ''
    let entreComillas = false
    for (const char of linea) {
      if (char === '"') {
        entreComillas = !entreComillas
      } else if (char === ',' && !entreComillas) {
        campos.push(actual.trim())
        actual = ''
      } else {
        actual += char
      }
    }
    campos.push(actual.trim())
    return campos
  })
}

function descargarTemplate() {
  const filas = [
    'nombre,precio,stock,unidad,categoria,emoji,codigoBarras',
    'Coca-Cola 600ml,20,24,pieza,Bebidas,🥤,7501055300105',
    'Tortillas kg,18,10,kg,Frescos,🌽,',
    'Jabón Palmolive,22,15,pieza,Limpieza,🧼,7509546066682',
  ]
  const csv = filas.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'template-inventario.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function SeccionImportar() {
  const inputRef = useRef(null)
  const [estado, setEstado] = useState('idle')
  const [mensaje, setMensaje] = useState('')
  const [progreso, setProgreso] = useState(null)

  function handleClickImportar() { setEstado('confirmando') }
  function handleCancelarConfirm() { setEstado('idle') }
  function handleConfirmarImport() { setEstado('idle'); inputRef.current?.click() }

  async function handleImportar(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setEstado('cargando')
    setMensaje('')
    setProgreso(null)

    try {
      const texto = await archivo.text()
      const filas = parsearCSV(texto)
      if (filas.length < 2) throw new Error('El archivo está vacío o sin datos')

      const headers = filas[0].map(h => h.toLowerCase())
      const iNombre   = headers.indexOf('nombre')
      const iPrecio   = headers.indexOf('precio')
      const iStock    = headers.indexOf('stock')
      const iUnidad   = headers.indexOf('unidad')
      const iCategoria= headers.indexOf('categoria')
      const iEmoji    = headers.indexOf('emoji')
      const iCodigo   = headers.indexOf('codigobarras')

      if (iNombre === -1 || iPrecio === -1 || iStock === -1) {
        throw new Error('El archivo debe tener columnas: nombre, precio, stock')
      }

      const existentes = await obtenerProductos()
      const datos = filas.slice(1)
      let actualizados = 0, creados = 0, errores = 0

      for (let i = 0; i < datos.length; i++) {
        const fila = datos[i]
        setProgreso({ actual: i + 1, total: datos.length })

        const nombreP = fila[iNombre] ?? ''
        const precio  = parseFloat(fila[iPrecio])
        const stock   = parseFloat(fila[iStock] ?? '0')

        if (!nombreP || isNaN(precio)) { errores++; continue }

        const unidad       = (fila[iUnidad] ?? 'pieza').toLowerCase() === 'kg' ? 'kg' : 'pieza'
        const emojiP       = fila[iEmoji] || '📦'
        const categoriaNom = fila[iCategoria]?.trim() || 'Otro'
        const codigoBarras = iCodigo !== -1 && fila[iCodigo] ? fila[iCodigo] : undefined

        const categoriaFinal = await obtenerOCrearCategoria(categoriaNom, emojiP)

        const nombreNorm = nombreP.trim().toLowerCase()
        const existente  = existentes.find(p =>
          (codigoBarras && p.codigoBarras && p.codigoBarras === codigoBarras) ||
          p.nombre.trim().toLowerCase() === nombreNorm
        )

        const stockFinal = existente
          ? (typeof existente.stock === 'number' ? existente.stock : 0) + stock
          : stock

        const producto = {
          ...(existente ? { _id: existente._id, _rev: existente._rev } : {}),
          nombre: nombreP, precio, stock: stockFinal, unidad,
          categoria: categoriaFinal, emoji: emojiP,
          ...(codigoBarras ? { codigoBarras } : {}),
        }

        try {
          await guardarProducto(producto)
          existente ? actualizados++ : creados++
        } catch { errores++ }
      }

      const partes = []
      if (creados > 0)     partes.push(`${creados} creado${creados !== 1 ? 's' : ''}`)
      if (actualizados > 0)partes.push(`${actualizados} actualizado${actualizados !== 1 ? 's' : ''}`)
      if (errores > 0)     partes.push(`${errores} omitido${errores !== 1 ? 's' : ''}`)
      setMensaje(partes.join(' · '))
      setEstado('exito')
    } catch (err) {
      setMensaje(err.message || 'Error al procesar el archivo')
      setEstado('error')
    } finally {
      setProgreso(null)
      e.target.value = ''
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-tienda-muted text-sm">
        Carga tu inventario desde una hoja de cálculo. Los productos existentes con el mismo nombre
        o código de barras serán actualizados, no duplicados.
      </p>

      <div className="space-y-3">
        {/* Descargar template */}
        <button
          onClick={descargarTemplate}
          className="w-full card p-4 flex items-center gap-4 active:scale-95 transition-transform"
        >
          <div className="w-12 h-12 rounded-2xl bg-tienda-accent/10 flex items-center justify-center text-tienda-accent flex-shrink-0">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-semibold text-tienda-text">Descargar plantilla CSV</p>
            <p className="text-tienda-muted text-xs">Plantilla con el formato correcto y ejemplos</p>
          </div>
        </button>

        {/* Columnas del template */}
        <div className="card p-4 space-y-2">
          <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wide">Columnas de la plantilla</p>
          <div className="grid grid-cols-2 gap-1">
            {[
              { col: 'nombre',       req: true },
              { col: 'precio',       req: true },
              { col: 'stock',        req: true },
              { col: 'unidad',       req: false, hint: 'pieza o kg' },
              { col: 'categoria',    req: false },
              { col: 'emoji',        req: false },
              { col: 'codigoBarras', req: false },
            ].map(({ col, req, hint }) => (
              <div key={col} className="flex items-center gap-1.5">
                <span className={`text-xs font-mono ${req ? 'text-tienda-accent' : 'text-tienda-muted'}`}>{col}</span>
                {req && <span className="text-tienda-danger text-xs">*</span>}
                {hint && <span className="text-tienda-muted text-xs">({hint})</span>}
              </div>
            ))}
          </div>
          <p className="text-tienda-muted text-xs mt-1">* Obligatorios</p>
        </div>

        {/* Importar CSV */}
        <button
          onClick={handleClickImportar}
          disabled={estado === 'cargando'}
          className="w-full card p-4 flex items-center gap-4 active:scale-95 transition-transform disabled:opacity-50"
        >
          <div className="w-12 h-12 rounded-2xl bg-tienda-accent/10 flex items-center justify-center text-tienda-accent flex-shrink-0">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            </svg>
          </div>
          <div className="text-left">
            <p className="font-semibold text-tienda-text">Importar desde CSV</p>
            <p className="text-tienda-muted text-xs">Actualiza o crea productos desde la plantilla</p>
          </div>
        </button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportar} />
      </div>

      {estado === 'cargando' && (
        <div className="space-y-2">
          <p className="text-center text-tienda-muted text-sm animate-pulse">
            {progreso ? `Importando ${progreso.actual} de ${progreso.total}…` : 'Procesando…'}
          </p>
          {progreso && (
            <div className="w-full bg-tienda-border rounded-full h-1.5">
              <div
                className="bg-tienda-accent h-1.5 rounded-full transition-all"
                style={{ width: `${(progreso.actual / progreso.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}
      {estado === 'exito' && (
        <div className="bg-tienda-success/10 border border-tienda-success/30 rounded-xl p-3">
          <p className="text-tienda-success text-sm font-medium">{mensaje}</p>
        </div>
      )}
      {estado === 'error' && (
        <div className="bg-tienda-danger/10 border border-tienda-danger/30 rounded-xl p-3">
          <p className="text-tienda-danger text-sm font-medium">{mensaje}</p>
        </div>
      )}

      {estado === 'confirmando' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-tienda-surface rounded-3xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <p className="text-4xl mb-3">📦</p>
              <h3 className="text-xl font-bold text-tienda-text">Importar inventario</h3>
              <p className="text-tienda-muted text-sm mt-2">
                Los productos del CSV se compararán con el inventario actual por{' '}
                <span className="text-tienda-text font-semibold">nombre</span> o{' '}
                <span className="text-tienda-text font-semibold">código de barras</span>.
              </p>
              <ul className="text-tienda-muted text-sm mt-3 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-tienda-success font-bold mt-0.5">✓</span>
                  <span>Si coincide → se <strong className="text-tienda-text">actualiza</strong> precio, stock y categoría</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-tienda-accent font-bold mt-0.5">+</span>
                  <span>Si no coincide → se <strong className="text-tienda-text">crea</strong> como producto nuevo</span>
                </li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button onClick={handleCancelarConfirm} className="btn-ghost flex-1 text-center text-base">Cancelar</button>
              <button onClick={handleConfirmarImport} className="btn-primary flex-1 text-center text-base">Continuar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── SECCIÓN CATEGORÍAS ───────────────────────────────────────

function SeccionCategorias() {
  const [categorias, setCategorias] = useState([])
  const [modal, setModal] = useState(null)
  const [errorCat, setErrorCat] = useState(null) // { id, motivo } | null
  const [confirmCat, setConfirmCat] = useState(null)

  useEffect(() => { cargar() }, [])

  function cargar() {
    obtenerCategorias().then(setCategorias)
  }

  async function handleGuardar(cat) {
    await guardarCategoria(cat)
    cargar()
    setModal(null)
  }

  async function handleEliminar(cat) {
    const resultado = await puedeEliminarCategoria(cat.nombre)
    if (!resultado.ok) {
      setErrorCat({ id: cat._id, motivo: resultado.motivo })
      return
    }
    setErrorCat(null)
    setConfirmCat(cat)
  }

  async function confirmarEliminarCat() {
    if (!confirmCat) return
    await eliminarCategoria(confirmCat)
    setConfirmCat(null)
    cargar()
  }

  return (
    <div className="space-y-3">
      <button
        onClick={() => setModal('nuevo')}
        className="w-full card p-4 flex items-center gap-3 text-tienda-accent active:scale-95 transition-transform border-dashed"
      >
        <div className="w-10 h-10 rounded-xl border-2 border-dashed border-tienda-accent/40 flex items-center justify-center text-xl">
          +
        </div>
        <span className="font-semibold text-sm">Nueva categoría</span>
      </button>

      {categorias.map(cat => (
        <div key={cat._id} className="card p-4 flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-3xl w-10 text-center">{cat.emoji}</span>
            <span className="flex-1 font-semibold text-tienda-text">{cat.nombre}</span>
            <button
              onClick={() => { setErrorCat(null); setModal(cat) }}
              className="w-9 h-9 rounded-xl bg-tienda-bg border border-tienda-border flex items-center justify-center text-tienda-muted active:scale-90"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              onClick={() => handleEliminar(cat)}
              className="w-9 h-9 rounded-xl bg-tienda-danger/10 border border-tienda-danger/30 flex items-center justify-center text-tienda-danger active:scale-90"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
            </button>
          </div>
          {errorCat?.id === cat._id && (
            <p className="text-tienda-danger text-xs leading-snug pl-1">{errorCat.motivo}</p>
          )}
        </div>
      ))}

      {modal && (
        <ModalCategoria
          categoria={modal === 'nuevo' ? null : modal}
          onGuardar={handleGuardar}
          onCancelar={() => setModal(null)}
        />
      )}

      {confirmCat && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-tienda-surface rounded-3xl w-full max-w-sm p-6 space-y-4">
            <div className="text-center">
              <p className="text-4xl mb-3">🗑️</p>
              <h3 className="text-xl font-bold text-tienda-text">Eliminar categoría</h3>
              <p className="text-tienda-muted mt-1">
                ¿Eliminar <strong className="text-tienda-text">"{confirmCat.nombre}"</strong>?
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmCat(null)} className="btn-ghost flex-1 text-center text-base">Cancelar</button>
              <button onClick={confirmarEliminarCat} className="btn-danger flex-1 text-center text-base">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sub-componentes comunes ──────────────────────────────────

function Header({ titulo, subtitulo, onVolver, onNuevo, stockBajoCount }) {
  return (
    <div className="p-4 border-b border-tienda-border bg-tienda-surface flex items-center gap-3 flex-shrink-0">
      {onVolver && (
        <button
          onClick={onVolver}
          className="w-10 h-10 rounded-xl bg-tienda-bg border border-tienda-border flex items-center justify-center text-tienda-muted active:scale-90 flex-shrink-0"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="text-xl font-bold text-tienda-text truncate">{titulo}</h1>
        <p className="text-tienda-muted text-sm">{subtitulo}</p>
      </div>
      {stockBajoCount > 0 && (
        <div className="flex items-center gap-1 bg-tienda-accent/10 border border-tienda-accent/30 rounded-xl px-2.5 py-1.5 flex-shrink-0">
          <span className="text-base">⚠️</span>
          <span className="text-tienda-accent text-sm font-semibold">{stockBajoCount}</span>
        </div>
      )}
      {onNuevo && (
        <button onClick={onNuevo} className="btn-primary px-4 py-2.5 text-base flex-shrink-0">
          + Nuevo
        </button>
      )}
    </div>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function ModalConfirmar({ producto, verificando, bloqueado, onConfirmar, onCancelar }) {
  if (!producto) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-tienda-surface rounded-3xl w-full max-w-sm p-6 space-y-4">

        {verificando ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-8 h-8 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
            <p className="text-tienda-muted text-sm">Verificando…</p>
          </div>
        ) : bloqueado ? (
          <>
            <div className="text-center">
              <p className="text-4xl mb-3">🚫</p>
              <h3 className="text-xl font-bold text-tienda-text">No se puede eliminar</h3>
              <p className="text-tienda-muted text-sm mt-2 leading-relaxed">{bloqueado}</p>
            </div>
            <button onClick={onCancelar} className="btn-ghost w-full text-center text-base">Entendido</button>
          </>
        ) : (
          <>
            <div className="text-center">
              <p className="text-4xl mb-3">🗑️</p>
              <h3 className="text-xl font-bold text-tienda-text">Eliminar producto</h3>
              <p className="text-tienda-muted mt-1">
                ¿Eliminar <strong className="text-tienda-text">"{producto.nombre}"</strong>?
                Esta acción no se puede deshacer.
              </p>
              {typeof producto.stock === 'number' && producto.stock > 0 && (
                <p className="text-tienda-accent text-sm mt-2 font-medium">
                  Tiene {producto.stock} unidad{producto.stock !== 1 ? 'es' : ''} en stock.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={onCancelar} className="btn-ghost flex-1 text-center text-base">Cancelar</button>
              <button onClick={onConfirmar} className="btn-danger flex-1 text-center text-base">Eliminar</button>
            </div>
          </>
        )}

      </div>
    </div>
  )
}
